import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatHours, formatPercent } from '../../SummaryPage/summaryFormatters';
import { isValidRange } from '../../SummaryPage/summaryPeriods';
import { DateTimeInput } from '../../../components/DateTimeInput';
import { toIsoWithOffset, toLocalInputValue } from '../../../utils/toIsoWithOffset';
import ScopeFilter from '../../../components/ScopeFilter/ScopeFilter';
import { useDraftScope } from '../../../hooks/useDraftScope';
import { useScopeRestore } from '../../../hooks/useScopeRestore';
import AnalyticsTable from '../../../components/AnalyticsTable/AnalyticsTable';
import { CONTROL_TABLE, createTableState } from '../../../utils/analyticsTableParams';
import {
  CONTROL_CUSTOM_PERIOD,
  CONTROL_DEFAULT_PERIOD,
  CONTROL_INVALID_PERIOD_TEXT,
  CONTROL_PERIOD_OPTIONS,
  applyControlParams,
  buildControlRange,
  describeCameraState,
  formatControlRange,
  loadSavedControlPeriod,
  parseControlParams,
  periodLabel,
  saveControlPeriod,
} from './profileControl';
import { useControlSummary } from './useControlSummary';
import ControlTimeline from './ControlTimeline';
import { useBusinessRulesCount } from './useBusinessRulesCount';

/*
 * ЛК → «Контроль»: «Уровень непрерывности контроля» (ТЗ №1 §11).
 * Не «SLA». Надёжность сервиса отделена от бизнес-insights «Сводки».
 *
 * ТЗ V5 №1 + №19: считается только применённый период. Вход без периода в URL
 * берёт последний применённый, иначе 7 дней — и это ровно один GET
 * /analytics/executive-summary: карточки и таблица «Камеры и простои» приходят
 * в одном ответе (period + scope + control_*). Пресеты и даты меняют только
 * черновик; новый расчёт — по «Применить», повтор без изменений не нужен.
 *
 * FE-FIX19 (SCOPE-MULTI01): тот же ScopeFilter, что в «Сводке», но состояние
 * своё — scope, период и состояние таблицы хранятся в /profile?tab=control.
 *
 * FE-FIX21 (ANALYTICS-TABLES01): таблица серверная — сортировка, фильтры и
 * страница уходят в control_* параметры того же запроса.
 *
 * FE-CORR-07 FE-001: период может прийти из «Обзора» (period=custom&from&to).
 * Битый или неполный custom не подменяется на 7 дней: запросов по нему нет, на
 * экране — просьба выбрать период.
 */

const SCOPE_CACHE_KEY = 'profile:control';

const CONTROL_COLUMNS = [
  { key: 'object', label: 'Объект', sortKey: 'object_name', render: (row) => row.object_name },
  { key: 'location', label: 'Локация', sortKey: 'location_name', render: (row) => row.location_name },
  { key: 'camera', label: 'Камера', sortKey: 'camera_name', render: (row) => row.camera_name },
  {
    key: 'status',
    label: 'Статус',
    sortKey: 'control_status',
    render: (row) => {
      const state = describeCameraState(row);
      return (
        <>
          <span className={`pp-dot pp-dot--${state.tone}`} />
          {state.label}
        </>
      );
    },
  },
  {
    key: 'technical_downtime',
    label: 'Тех. простой',
    sortKey: 'technical_downtime_hours',
    numeric: true,
    compact: true,
    render: (row) => formatHours(row.technical_downtime_hours),
  },
  {
    key: 'manual_stopped',
    label: 'Ручная остановка',
    sortKey: 'manual_stopped_hours',
    numeric: true,
    compact: true,
    render: (row) => formatHours(row.manual_stopped_hours),
  },
  {
    key: 'available',
    label: 'Часы контроля',
    sortKey: 'available_control_hours',
    numeric: true,
    compact: true,
    render: (row) => formatHours(row.available_control_hours),
  },
  {
    key: 'continuity',
    label: 'Непрерывность',
    sortKey: 'continuity_percent',
    numeric: true,
    compact: true,
    render: (row) => formatPercent(row.continuity_percent),
  },
  {
    key: 'technical_continuity',
    label: 'Тех. доступность',
    sortKey: 'technical_continuity_percent',
    numeric: true,
    compact: true,
    render: (row) => formatPercent(row.technical_continuity_percent),
  },
];

const ProfileControlTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseControlParams(searchParams), [searchParams]);

  // Порядок выбора периода: URL → сохранённый применённый → 7 дней.
  const saved = useMemo(() => (params.hasPeriod ? null : loadSavedControlPeriod()), [params.hasPeriod]);
  const appliedPeriod = params.hasPeriod ? params.period : (saved?.period || CONTROL_DEFAULT_PERIOD);
  const appliedFrom = params.hasPeriod ? params.from : (saved?.from || '');
  const appliedTo = params.hasPeriod ? params.to : (saved?.to || '');
  const isCustom = appliedPeriod === CONTROL_CUSTOM_PERIOD;
  const customValid = isCustom && isValidRange(appliedFrom, appliedTo);
  const customInvalid = isCustom && !customValid;

  // Границы пресета фиксируются при применении: перерисовка, сортировка или
  // страница таблицы не сдвигают to=now и не создают «новый» период.
  const range = useMemo(() => {
    if (appliedPeriod === CONTROL_CUSTOM_PERIOD) {
      return isValidRange(appliedFrom, appliedTo) ? { from: appliedFrom, to: appliedTo } : null;
    }
    return buildControlRange(Number(appliedPeriod));
  }, [appliedPeriod, appliedFrom, appliedTo]);

  const [draftPeriod, setDraftPeriod] = useState(appliedPeriod);
  const [draftFrom, setDraftFrom] = useState(appliedFrom ? toLocalInputValue(appliedFrom) : '');
  const [draftTo, setDraftTo] = useState(appliedTo ? toLocalInputValue(appliedTo) : '');
  // Применённый период сменился (Back/Forward, переход из «Обзора») — черновик за ним.
  useEffect(() => {
    setDraftPeriod(appliedPeriod);
    if (appliedPeriod === CONTROL_CUSTOM_PERIOD) {
      setDraftFrom(appliedFrom ? toLocalInputValue(appliedFrom) : '');
      setDraftTo(appliedTo ? toLocalInputValue(appliedTo) : '');
    }
  }, [appliedPeriod, appliedFrom, appliedTo]);

  const withApplied = useCallback((partial) => applyControlParams(searchParams, {
    ...params, period: appliedPeriod, from: appliedFrom, to: appliedTo, ...partial,
  }), [params, appliedPeriod, appliedFrom, appliedTo, searchParams]);

  const updateParams = useCallback((partial) => {
    setSearchParams(withApplied(partial));
  }, [withApplied, setSearchParams]);

  // ТЗ №05: вход без scope в URL возвращает последний выбор Контроля.
  const { restorableScope, remember: rememberScope } = useScopeRestore(SCOPE_CACHE_KEY, params.scope);
  useEffect(() => {
    if (!restorableScope) return;
    setSearchParams(withApplied({ scope: restorableScope }), { replace: true });
  }, [restorableScope, withApplied, setSearchParams]);

  // Пока scope восстанавливается из кэша, считать по пустому scope незачем:
  // это был бы лишний тяжёлый запрос, который тут же устареет.
  const requestEnabled = Boolean(range) && !restorableScope;
  const summary = useControlSummary({
    range,
    scope: params.scope,
    tableState: params.table,
    enabled: requestEnabled,
  });
  const businessRules = useBusinessRulesCount({ scope: params.scope, enabled: requestEnabled });

  const draftIsCustom = draftPeriod === CONTROL_CUSTOM_PERIOD;
  const draftFromIso = draftIsCustom ? (toIsoWithOffset(draftFrom) || '') : '';
  const draftToIso = draftIsCustom ? (toIsoWithOffset(draftTo) || '') : '';
  const periodDirty = draftPeriod !== appliedPeriod
    || (draftIsCustom && (draftFromIso !== appliedFrom || draftToIso !== appliedTo));
  const draftReady = !draftIsCustom || (Boolean(draftFrom) && Boolean(draftTo));

  const applyPeriod = () => {
    if (!periodDirty || !draftReady) return;
    const next = { period: draftPeriod, from: draftFromIso, to: draftToIso };
    if (!draftIsCustom || isValidRange(next.from, next.to)) saveControlPeriod(next);
    // Смена периода — другой набор строк: страница таблицы начинается заново.
    updateParams({ ...next, table: createTableState(CONTROL_TABLE) });
  };

  const editDate = (setter) => (value) => {
    setter(value);
    setDraftPeriod(CONTROL_CUSTOM_PERIOD);
  };

  // Смена области данных сбрасывает локальные фильтры таблицы: фильтр по
  // камере, которой в новом scope нет, показывал бы пустую таблицу без причины.
  const updateScope = useCallback(
    (scope) => {
      rememberScope(scope);
      updateParams({ scope, table: createTableState(CONTROL_TABLE) });
    },
    [rememberScope, updateParams]
  );
  // Галочки меняют черновик: расчёт только по «Применить».
  const scopeDraft = useDraftScope(params.scope, updateScope);

  const renderFilters = () => (
    <div className="pp-card pp-control-filters">
      {/* Фильтр всегда раскрыт: сводку выбора и «Сбросить всё» показывает
          сам ScopeFilter, отдельная кнопка-переключатель не нужна. */}
      <ScopeFilter
        value={scopeDraft.draft}
        onChange={scopeDraft.setDraft}
        onApply={scopeDraft.apply}
        applyDisabled={!scopeDraft.dirty}
        idPrefix="pp-scope"
      />

      <div className="pp-control-period">
        <span className="pp-field-label">Период</span>
        <div className="pp-segment" role="group" aria-label="Период">
          {CONTROL_PERIOD_OPTIONS.filter((option) => option.id !== CONTROL_CUSTOM_PERIOD).map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={draftPeriod === option.id}
              className={`pp-seg-btn${draftPeriod === option.id ? ' pp-seg-btn--active' : ''}`}
              onClick={() => setDraftPeriod(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="pp-control-custom">
          <DateTimeInput id="pp-control-from" value={draftFrom} onChange={editDate(setDraftFrom)} ariaLabel="Начало периода" />
          <span>—</span>
          <DateTimeInput id="pp-control-to" value={draftTo} onChange={editDate(setDraftTo)} ariaLabel="Конец периода" />
          <button
            type="button"
            className="pp-btn pp-btn--primary"
            disabled={!periodDirty || !draftReady}
            onClick={applyPeriod}
          >
            Применить
          </button>
        </div>
        {customInvalid && <p className="pp-control-error" role="alert">{CONTROL_INVALID_PERIOD_TEXT}</p>}
      </div>
    </div>
  );

  if (customInvalid) {
    return <div className="pp-control">{renderFilters()}</div>;
  }

  if (summary.status === 'error') {
    return (
      <div className="pp-control">
        {renderFilters()}
        <div className="pp-card pp-state pp-state--error" role="alert">
          <p>Не удалось загрузить данные контроля</p>
          <button type="button" className="pp-btn pp-btn--primary" onClick={summary.reload}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  const data = summary.data;
  if (!data) {
    return (
      <div className="pp-control">
        {renderFilters()}
        <div className="pp-card" role="status" aria-live="polite">Загрузка…</div>
      </div>
    );
  }

  const continuity = data.control_continuity || {};
  const kpis = data.kpis || {};
  const loading = summary.status === 'loading';
  const rangeLabel = isCustom
    ? formatControlRange(appliedFrom, appliedTo)
    : periodLabel(Number(appliedPeriod));
  const appliedLabel = isCustom ? rangeLabel : `За ${rangeLabel}`;

  return (
    <div className={`pp-control${loading ? ' pp-control--stale' : ''}`} aria-busy={loading}>
      {renderFilters()}

      <div className="pp-kpis">
        <div className="pp-kpi"><span>Камеры онлайн сейчас</span><b>{continuity.cameras_online_now ?? '—'}</b></div>
        <div className="pp-kpi"><span>Оффлайн сейчас</span><b>{continuity.cameras_offline_now ?? '—'}</b></div>
        <div className="pp-kpi"><span>Остановлены вручную</span><b>{continuity.cameras_stopped_now ?? '—'}</b></div>
        <div className="pp-kpi"><span>Отслеживаемые нарушения</span><b>{kpis.active_rules ?? '—'}</b></div>
        {!businessRules.hidden && (
          <div className="pp-kpi"><span>Отслеживаемые бизнес-метрики</span><b>{businessRules.value ?? '—'}</b></div>
        )}
        <div className="pp-kpi">
          <span>Покрытие контроля</span>
          <b>{formatPercent(kpis.coverage_percent)}</b>
          {kpis.total_cameras != null && <small>{`${kpis.monitored_cameras} из ${kpis.total_cameras} камер`}</small>}
        </div>
      </div>

      <div className="pp-card">
        <h3 className="pp-card-title">{appliedLabel}</h3>
        <div className="pp-periods">
          <div className="pp-period">
            <div className="pp-period-label">Уровень непрерывности контроля</div>
            <div className="pp-period-value">{formatPercent(continuity.continuity_percent)}</div>
          </div>
          <div className="pp-period">
            <div className="pp-period-label">Техническая доступность</div>
            <div className="pp-period-value">{formatPercent(continuity.technical_continuity_percent)}</div>
          </div>
          <div className="pp-period">
            <div className="pp-period-label">Нарушения</div>
            <div className="pp-period-value">{kpis.violations_count ?? '—'}</div>
          </div>
        </div>
      </div>

      {/* ТЗ V5 №11: Гант над таблицей — те же камеры, порядок и период. */}
      <ControlTimeline
        range={range}
        scope={params.scope}
        tableState={params.table}
        enabled={requestEnabled}
      />

      <AnalyticsTable
        table={CONTROL_TABLE}
        state={params.table}
        onChange={(next) => updateParams({ table: next })}
        payload={continuity.camera_table || null}
        columns={CONTROL_COLUMNS}
        rowKey={(row) => row.camera_id}
        title={`Камеры и простои · ${rangeLabel}`}
        note="Непрерывность — фактический контроль (ручная остановка его снижает). Техническая доступность — без учёта ручных остановок."
        scope={params.scope}
        disabled={loading}
        emptyText="Камер по этим фильтрам нет"
        idPrefix="pp-control-table"
      />
    </div>
  );
};

export default ProfileControlTab;
